import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

import Input from '../../../components/ui/Input';
import { SystemPreferences } from '../types';

interface SystemPreferencesSectionProps {
  preferences: SystemPreferences;
  onUpdate: (preferences: SystemPreferences) => void;
}

const SystemPreferencesSection = ({ preferences, onUpdate }: SystemPreferencesSectionProps) => {
  const [formData, setFormData] = useState(preferences);
  const [hasChanges, setHasChanges] = useState(false);

  const layoutOptions = [
    { value: 'compact', label: 'Compacto' },
    { value: 'detailed', label: 'Detalhado' },
    { value: 'minimal', label: 'Minimalista' }
  ];

  const viewOptions = [
    { value: 'table', label: 'Tabela' },
    { value: 'cards', label: 'Cards' }
  ];

  const itemsPerPageOptions = [
    { value: 10, label: '10 itens' },
    { value: 25, label: '25 itens' },
    { value: 50, label: '50 itens' },
    { value: 100, label: '100 itens' }
  ];

  const dateFormatOptions = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/AAAA' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/AAAA' }
  ];

  const exportFormatOptions = [
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'csv', label: 'CSV (.csv)' },
    { value: 'pdf', label: 'PDF (.pdf)' }
  ];

  const themeOptions = [
    { value: 'light', label: 'Claro' },
    { value: 'dark', label: 'Escuro' },
    { value: 'auto', label: 'Automático' }
  ];

  const handleChange = (field: keyof SystemPreferences, value: any) => {
    setFormData({ ...formData, [field]: value });
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(formData);
    setHasChanges(false);
  };

  const handleReset = () => {
    setFormData(preferences);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Preferências do Sistema</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Personalize a interface e comportamento do sistema
          </p>
        </div>
        {hasChanges && (
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleReset}>
              Descartar
            </Button>
            <Button variant="default" iconName="Save" iconPosition="left" onClick={handleSave}>
              Salvar Preferências
            </Button>
          </div>
        )}
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Layout" size={20} color="var(--color-primary)" />
          Aparência e Layout
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Layout do Dashboard"
            description="Escolha como visualizar o painel principal"
            options={layoutOptions}
            value={formData.dashboardLayout}
            onChange={(value) => handleChange('dashboardLayout', value)}
          />

          <Select
            label="Visualização Padrão de Consultas"
            description="Formato de exibição das consultas"
            options={viewOptions}
            value={formData.defaultConsultationView}
            onChange={(value) => handleChange('defaultConsultationView', value)}
          />

          <Select
            label="Tema"
            description="Aparência visual do sistema"
            options={themeOptions}
            value={formData.theme}
            onChange={(value) => handleChange('theme', value)}
          />

          <Select
            label="Itens por Página"
            description="Quantidade de registros exibidos"
            options={itemsPerPageOptions}
            value={formData.itemsPerPage}
            onChange={(value) => handleChange('itemsPerPage', Number(value))}
          />
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
          Formatos e Padrões
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Formato de Data"
            description="Padrão de exibição de datas"
            options={dateFormatOptions}
            value={formData.dateFormat}
            onChange={(value) => handleChange('dateFormat', value)}
          />

          <Select
            label="Formato de Exportação Padrão"
            description="Tipo de arquivo para downloads"
            options={exportFormatOptions}
            value={formData.exportFormat}
            onChange={(value) => handleChange('exportFormat', value)}
          />

          <Input
            label="Intervalo de Auto-Salvamento"
            type="number"
            value={formData.autoSaveInterval}
            onChange={(e) => handleChange('autoSaveInterval', Number(e.target.value))}
            description="Minutos entre salvamentos automáticos"
            min={1}
            max={60}
          />
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Bookmark" size={20} color="var(--color-primary)" />
          Filtros Salvos
        </h3>
        <div className="space-y-3">
          {[
            { name: 'Consultas Pendentes', count: 12 },
            { name: 'Erros de API', count: 3 },
            { name: 'Últimos 30 Dias', count: 156 }
          ].map((filter) => (
            <div
              key={filter.name}
              className="flex items-center justify-between p-3 bg-muted rounded-md"
            >
              <div className="flex items-center gap-3">
                <Icon name="Filter" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm font-medium text-foreground">{filter.name}</span>
                <span className="text-xs text-muted-foreground">({filter.count} resultados)</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-1 hover:bg-background rounded transition-colors"
                  aria-label="Editar filtro"
                >
                  <Icon name="Edit" size={14} color="var(--color-muted-foreground)" />
                </button>
                <button
                  className="p-1 hover:bg-background rounded transition-colors"
                  aria-label="Remover filtro"
                >
                  <Icon name="Trash2" size={14} color="var(--color-destructive)" />
                </button>
              </div>
            </div>
          ))}
          <Button variant="outline" iconName="Plus" iconPosition="left" fullWidth>
            Criar Novo Filtro
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} color="var(--color-primary)" />
          Templates de Exportação
        </h3>
        <div className="space-y-3">
          {[
            { name: 'Relatório Completo', format: 'xlsx' },
            { name: 'Resumo Executivo', format: 'pdf' },
            { name: 'Dados Brutos', format: 'csv' }
          ].map((template) => (
            <div
              key={template.name}
              className="flex items-center justify-between p-3 bg-muted rounded-md"
            >
              <div className="flex items-center gap-3">
                <Icon name="FileDown" size={16} color="var(--color-muted-foreground)" />
                <span className="text-sm font-medium text-foreground">{template.name}</span>
                <span className="text-xs text-muted-foreground uppercase">
                  .{template.format}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="p-1 hover:bg-background rounded transition-colors"
                  aria-label="Editar template"
                >
                  <Icon name="Edit" size={14} color="var(--color-muted-foreground)" />
                </button>
                <button
                  className="p-1 hover:bg-background rounded transition-colors"
                  aria-label="Remover template"
                >
                  <Icon name="Trash2" size={14} color="var(--color-destructive)" />
                </button>
              </div>
            </div>
          ))}
          <Button variant="outline" iconName="Plus" iconPosition="left" fullWidth>
            Criar Novo Template
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemPreferencesSection;